import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import {
  defaultLandingContent,
  mergeLandingContent,
  type LandingContent,
} from "@/data/landingContent";

// The landing page reads its content from the CMS. When the request fails (or
// nothing has been saved yet) we fall back to the built-in defaults so the page
// always renders.
export function useLandingContent(): LandingContent {
  const query = useQuery({
    queryKey: ["landing-content"],
    queryFn: async () => {
      const res = await apiClient.get("/public/content/landing");
      return mergeLandingContent(defaultLandingContent, res.data?.data ?? {});
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return query.data ?? defaultLandingContent;
}

// Admin editor: reads the raw saved content merged over defaults.
export function useAdminLandingContent() {
  return useQuery({
    queryKey: ["admin-landing-content"],
    queryFn: async () => {
      const res = await apiClient.get("/admin/content/landing");
      return mergeLandingContent(defaultLandingContent, res.data?.data ?? {});
    },
  });
}

export function useSaveLandingContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: LandingContent) => {
      const res = await apiClient.put("/admin/content/landing", content);
      return res.data?.data as unknown;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-content"] });
      queryClient.invalidateQueries({ queryKey: ["admin-landing-content"] });
    },
  });
}
